import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Brain, ChevronLeft, Video, Mic, Calendar, User, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  adult:          { ar: 'الصحة النفسية للبالغين',  en: 'Adult Mental Health' },
  child_teen:     { ar: 'صحة الأطفال والمراهقين', en: 'Child & Adolescent'   },
  family_couples: { ar: 'الإرشاد الأسري والزوجي', en: 'Family & Couples'     },
};

/**
 * PsychologyDoctorsPage
 *
 * Gate 1 — Requires an active psychology purchase (via `/api/psychology/my-purchase`).
 *          If none, redirect to /psychology.
 * Gate 2 — Reads the selected sub-category from localStorage (set by the
 *          PsychologyCategoryPage before payment) and asks the API for
 *          doctors filtered to that category. Falls back to all psychology
 *          doctors if no category is set.
 *
 * Booking uses `POST /api/psychology/appointments` which decrements the
 * purchase's `sessions_used`.
 */
export const PsychologyDoctorsPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isRTL = language === 'ar';

  const [purchase, setPurchase] = useState(null);
  const [purchaseLoaded, setPurchaseLoaded] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);

  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [step, setStep] = useState(1);   // 1 = appointment info, 2 = choose doctor
  const [book, setBook] = useState({
    appointment_date: '',
    appointment_time: '',
    consultation_type: 'video',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const catLabel = useMemo(
    () => (category ? (language === 'ar' ? CATEGORY_LABELS[category]?.ar : CATEGORY_LABELS[category]?.en) : ''),
    [category, language],
  );

  useEffect(() => {
    if (!user) {
      navigate('/login?next=/psychology/doctors');
      return;
    }
    let cat = null;
    try { cat = localStorage.getItem('tababa_psy_category'); } catch { /* ignore */ }
    setCategory(cat);

    // 1) Verify active purchase
    axios.get(`${API}/psychology/my-purchase`, { withCredentials: true })
      .then((r) => {
        setPurchase(r.data?.active ? r.data : null);
      })
      .catch(() => setPurchase(null))
      .finally(() => setPurchaseLoaded(true));

    // 2) Load doctors (filtered when possible)
    const url = cat ? `${API}/psychology/doctors?category=${cat}` : `${API}/psychology/doctors`;
    axios.get(url)
      .then((r) => setDoctors(r.data || []))
      .catch(() => setDoctors([]))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const proceedToDoctors = () => {
    if (!book.appointment_date || !book.appointment_time) {
      toast.error(t('اختر التاريخ والوقت أولاً', 'Please pick a date and time first'));
      return;
    }
    const chosen = new Date(`${book.appointment_date}T${book.appointment_time}`);
    if (isNaN(chosen.getTime()) || chosen.getTime() < Date.now() - 60_000) {
      toast.error(t('اختر تاريخاً في المستقبل', 'Please pick a future date'));
      return;
    }
    setStep(2);
  };

  const bookWithDoctor = async (doctor) => {
    // Book straight away using the info collected in step 1.
    setSelectedDoctor(doctor);
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/psychology/appointments`, {
        doctor_id: doctor._id,
        ...book,
      }, { withCredentials: true });
      toast.success(t(`تم حجز الموعد مع د. ${doctor.full_name}`, `Appointment booked with Dr. ${doctor.full_name}`));
      setPurchase((p) => p ? { ...p, sessions_remaining: r.data.sessions_remaining, sessions_used: (p.sessions_used || 0) + 1 } : p);
      navigate('/patient');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحجز', 'Booking failed'));
      setSelectedDoctor(null);
    } finally {
      setSubmitting(false);
    }
  };

  // Purchase gate
  if (purchaseLoaded && !purchase) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-blue-50 to-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className="p-8 max-w-md w-full text-center" data-testid="psy-doctors-locked">
          <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4">
            <Brain size={26} />
          </div>
          <h2 className="text-xl font-extrabold text-[#0d2c54] mb-2">
            {t('يرجى شراء باقة أولاً', 'Please purchase a package first')}
          </h2>
          <p className="text-gray-600 mb-5 text-sm">
            {t('لبدء الجلسة يجب شراء باقة نفسية.', 'To book a session, please purchase a psychology package first.')}
          </p>
          <Button onClick={() => navigate('/psychology')} className="bg-[#1565c0] hover:bg-[#0d47a1] w-full h-12 rounded-full font-bold" data-testid="go-to-psychology-btn">
            {t('عرض الأقسام', 'View Categories')}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'} data-testid="psychology-doctors-page">
      <header className="bg-gradient-to-r from-[#1565c0] to-[#0d47a1] text-white px-4 py-6 shadow-md">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          {step === 2 ? (
            <button
              type="button"
              onClick={() => setStep(1)}
              className="hover:bg-white/15 rounded-full p-2 transition"
              data-testid="back-to-info-btn"
              aria-label="back"
            >
              <ChevronLeft size={22} className={isRTL ? 'rotate-180' : ''} />
            </button>
          ) : (
            <Link to="/psychology" className="hover:bg-white/15 rounded-full p-2 transition" data-testid="back-psychology-link">
              <ChevronLeft size={22} className={isRTL ? 'rotate-180' : ''} />
            </Link>
          )}
          <Brain size={26} />
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-extrabold tracking-tight" data-testid="psy-step-title">
              {step === 1
                ? t('معلومات الموعد', 'Appointment Info')
                : t('اختر الأخصائي', 'Choose your Specialist')}
            </h1>
            {catLabel && (
              <p className="text-xs opacity-90">{catLabel}</p>
            )}
          </div>
          {/* Step indicator */}
          <div className="hidden sm:flex items-center gap-2 text-xs" data-testid="psy-step-indicator">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${step === 1 ? 'bg-white text-[#1565c0]' : 'bg-white/25 text-white'}`}>1</span>
            <span className="w-6 h-px bg-white/40" />
            <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${step === 2 ? 'bg-white text-[#1565c0]' : 'bg-white/25 text-white'}`}>2</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Sessions remaining */}
        {purchase && (
          <Card className="p-4 mb-5 border border-emerald-200 bg-emerald-50/70" data-testid="psy-sessions-summary">
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div>
                <p className="text-sm font-bold text-emerald-800">
                  {t(purchase.package_name_ar || 'باقة نفسية', purchase.package_name_en || 'Psychology Package')}
                </p>
                <p className="text-[11px] text-emerald-700 mt-0.5 flex items-center gap-1">
                  <Sparkles size={12} /> {t('تم الدفع', 'Paid')}
                </p>
              </div>
              <div className="text-end">
                <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">
                  {t('جلسات متبقية', 'Sessions left')}
                </p>
                <p className="text-2xl font-extrabold text-emerald-700 leading-none" data-testid="psy-sessions-remaining">
                  {purchase.sessions_remaining}
                  <span className="text-sm font-semibold text-emerald-600/80 ml-1">/{purchase.sessions_total}</span>
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* STEP 1 — appointment info */}
        {step === 1 && (
          <Card className="p-5 space-y-4" data-testid="psy-info-form">
            <div>
              <p className="font-bold text-slate-800 mb-1">
                {t('اختر تاريخاً ووقتاً مناسبين', 'Pick a date and time that work for you')}
              </p>
              <p className="text-xs text-slate-500">
                {t('بعدها ستختار الأخصائي المناسب.', 'You will pick the right specialist next.')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('التاريخ', 'Date')}</Label>
                <Input
                  type="date"
                  value={book.appointment_date}
                  onChange={(e) => setBook({ ...book, appointment_date: e.target.value })}
                  min={new Date().toISOString().slice(0, 10)}
                  className="mt-1"
                  data-testid="psy-book-date"
                />
              </div>
              <div>
                <Label className="text-xs">{t('الوقت', 'Time')}</Label>
                <Input
                  type="time"
                  value={book.appointment_time}
                  onChange={(e) => setBook({ ...book, appointment_time: e.target.value })}
                  className="mt-1"
                  data-testid="psy-book-time"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs mb-1 block">{t('نوع الجلسة', 'Session type')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: 'video', ar: 'فيديو', en: 'Video', Icon: Video },
                  { v: 'audio', ar: 'صوت',   en: 'Voice', Icon: Mic },
                ].map(({ v, ar, en, Icon }) => {
                  const active = book.consultation_type === v;
                  return (
                    <button
                      type="button"
                      key={v}
                      onClick={() => setBook({ ...book, consultation_type: v })}
                      className={`h-11 rounded-xl border-2 font-bold text-sm flex items-center justify-center gap-2 transition ${
                        active ? 'border-[#1565c0] bg-[#e3f2fd] text-[#0d47a1]' : 'border-gray-200 bg-white text-gray-600'
                      }`}
                      data-testid={`psy-book-type-${v}`}
                    >
                      <Icon size={16} /> {t(ar, en)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs">{t('ملاحظات (اختياري)', 'Notes (optional)')}</Label>
              <Textarea
                value={book.notes}
                onChange={(e) => setBook({ ...book, notes: e.target.value })}
                rows={3}
                className="mt-1 resize-none"
                data-testid="psy-book-notes"
                placeholder={t('ما تريد مشاركته قبل الجلسة', 'Anything to share with the specialist before the session')}
              />
            </div>

            <Button
              onClick={proceedToDoctors}
              disabled={!book.appointment_date || !book.appointment_time}
              className="w-full h-12 rounded-full bg-[#1565c0] hover:bg-[#0d47a1] font-bold"
              data-testid="psy-proceed-to-doctors"
            >
              {t('التالي — اختيار الأخصائي', 'Next — Choose specialist')}
            </Button>
          </Card>
        )}

        {/* STEP 2 — pick doctor (books immediately on click) */}
        {step === 2 && (
          <>
            {/* Recap of chosen info */}
            <Card className="p-3 mb-4 flex items-center justify-between bg-blue-50/60 border-blue-100" data-testid="psy-info-recap">
              <div className="text-sm text-slate-700 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <Calendar size={14} className="text-[#1565c0]" />
                  <span className="font-bold">{book.appointment_date}</span>
                  <span>·</span>
                  <span className="font-bold">{book.appointment_time}</span>
                </span>
                <span className="inline-flex items-center gap-1 text-xs bg-white px-2 py-0.5 rounded-full border">
                  {book.consultation_type === 'video' ? <Video size={12} /> : <Mic size={12} />}
                  {book.consultation_type === 'video' ? t('فيديو', 'Video') : t('صوت', 'Voice')}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs text-[#1565c0] font-bold hover:underline"
                data-testid="edit-info-btn"
              >
                {t('تعديل', 'Edit')}
              </button>
            </Card>

            {loading ? (
              <div className="text-center py-16 text-slate-500 text-sm">
                {t('جاري التحميل...', 'Loading...')}
              </div>
            ) : doctors.length === 0 ? (
              <Card className="p-8 text-center" data-testid="psy-doctors-empty">
                <User className="text-slate-400 mx-auto mb-3" size={32} />
                <p className="text-slate-700 font-bold">
                  {t('لا يوجد أطباء متاحون حالياً', 'No available doctors right now')}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {t('يرجى المحاولة لاحقاً أو التواصل مع فريقنا.', 'Please try later or contact our team.')}
                </p>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="psy-doctors-list">
                {doctors.map((doc) => (
                  <Card key={doc._id} className="p-4 flex items-center gap-3" data-testid={`psy-doctor-${doc._id}`}>
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#22c8b8] to-[#1565c0] text-white flex items-center justify-center flex-shrink-0">
                      {doc.profile_photo ? (
                        <img src={doc.profile_photo} alt={doc.full_name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <User size={22} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#0d2c54] truncate">{doc.full_name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {doc.specialty || t('أخصائي نفسي', 'Psychology specialist')}
                      </p>
                      {doc.psychology_category && (
                        <span className="inline-block mt-1 text-[10px] font-semibold text-[#1565c0] bg-blue-50 px-2 py-0.5 rounded-full">
                          {language === 'ar'
                            ? CATEGORY_LABELS[doc.psychology_category]?.ar
                            : CATEGORY_LABELS[doc.psychology_category]?.en}
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={() => bookWithDoctor(doc)}
                      disabled={submitting || !purchase || purchase.sessions_remaining <= 0}
                      className="bg-[#1565c0] hover:bg-[#0d47a1] rounded-full font-bold px-5"
                      data-testid={`book-psy-doctor-${doc._id}`}
                    >
                      {submitting && selectedDoctor?._id === doc._id
                        ? t('جاري...', 'Booking...')
                        : t('احجز', 'Book')}
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default PsychologyDoctorsPage;
